import React from 'react';
import { NextSeo } from 'next-seo';
import PropTypes from 'prop-types';
import { CurrentItemPage } from '../../components/Views/CurrentItemPage';
import Layout from '../../components/Layout/Layout';
import { normalizePath, hostname } from '../../utils/helpers';
import config from '../../config';
import initApollo from '../../utils/init-Apollo';
import { getCurrentItemData, getResStatusCode } from '../../utils/helpers/item';
import { generateItemSeoObj } from '../../utils/helpers/seo';
import { withBugsnagGetStaticProps } from '../../utils/withBugsnag';
import { extractApolloState } from '../../utils/helpers/extractApolloState';

const CurrentItem = props => {
  const mainPageImage = props?.mainPageImage?.full;
  const isSoftError = props?.isSoftError;

  return (
    <>
      {(mainPageImage || isSoftError) && (
        <NextSeo
          noindex={isSoftError}
          nofollow={false}
          robotsProps={
            mainPageImage
              ? { maxImagePreview: isSoftError ? 'none' : 'large' }
              : {}
          }
        />
      )}
      <CurrentItemPage {...props} />
    </>
  );
};

CurrentItem.propTypes = {
  mainPageImage: PropTypes.shape({
    full: PropTypes.string
  }),
  isSoftError: PropTypes.bool,
  isrVersion: PropTypes.string,
  itemId: PropTypes.string
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}

export const getStaticProps = withBugsnagGetStaticProps(
  async ({ locale, params }) => {
    const apolloClient = initApollo({}, { lang: locale });

    const itemId = params.id;
    const currentAsPath = `/items/${itemId}`;
    const newAsPath = normalizePath(currentAsPath);
    const { protocol } = config;
    const host = hostname(locale, config.host);
    const hostSum = {
      currentUrl: `${protocol}://${host}${newAsPath}`,
      protocol,
      host,
      path: newAsPath
    };

    if (
      process.env.NEXT_PUBLIC_E2E === '1' &&
      process.env.NODE_ENV !== 'production'
    ) {
      const seoData = generateItemSeoObj({}, newAsPath, hostSum);
      return {
        props: {
          newAsPath,
          hostSum,
          itemId,
          seoData,
          currentItemData: null,
          mainPageImage: {},
          currentLang: locale,
          isrVersion: null
        }
      };
    }

    if (!currentAsPath.match(/\d+/)?.[0]) return { notFound: true };

    const currentItemRes = await getCurrentItemData(apolloClient, itemId);
    const { currentItemData, errorCode } = currentItemRes;

    const statusCode = getResStatusCode(currentItemData);

    const seoData = generateItemSeoObj(currentItemData, newAsPath, hostSum);

    return extractApolloState(apolloClient, {
      props: {
        newAsPath,
        hostSum,
        itemId,
        seoData,
        mainPageImage: currentItemData?.photos?.[0] || {},
        currentLang: locale,
        isSoftError: !!statusCode || !!errorCode || false,
        ...currentItemRes
      },
      revalidate: 3600
    });
  },
  '/items/[id]'
);

CurrentItem.getLayout = (page, pageProps) => (
  <Layout
    isSafeAreaTop
    isBottomNavigation={false}
    isCurrentItemPage
    isBanner
    pageProps={pageProps}
    isBackgroundBanner
    hideMobileHeader
  >
    {page}
  </Layout>
);

export default CurrentItem;
